/**
 * SubmissionList Component
 * Displays list of submissions with filters
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useSubmissions } from '../../hooks/useSubmissions';
import { Loading } from '../Common/Loading';
import { ErrorMessage } from '../Common/ErrorMessage';
import { SubmissionStatusBadge } from './SubmissionStatusBadge';
import {
  SUBMISSION_STATUS_LABELS,
  DEFAULT_PAGE_SIZE
} from '../../utils/constants';
import { format } from 'date-fns';

export const SubmissionList = ({ onSelectSubmission, selectedId }) => {
  const searchInputRef = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  // Separate state for search input (debounced)
  const [searchInput, setSearchInput] = useState('');

  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  // Track if search input had focus
  const [searchWasFocused, setSearchWasFocused] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      // Remember if search was focused before triggering refetch
      setSearchWasFocused(document.activeElement === searchInputRef.current);
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPage(0);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error, refetch } = useSubmissions({
    ...filters,
    limit: rowsPerPage,
    offset: page * rowsPerPage
  });

  // Refocus search input after data loads if it was focused before
  useEffect(() => {
    if (searchWasFocused && searchInputRef.current && !isLoading) {
      searchInputRef.current.focus();
      setSearchWasFocused(false);
    }
  }, [isLoading, searchWasFocused]);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) return <Loading message="Loading submissions..." />;
  if (error) return <ErrorMessage error={error} onRetry={refetch} />;

  const submissions = data?.submissions || [];
  const totalCount = data?.totalCount || 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Submissions</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={refetch} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Search"
            placeholder="Game name or submitter..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            inputRef={searchInputRef}
            size="small"
            sx={{ flex: 1 }}
          />

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {Object.entries(SUBMISSION_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Game Name</TableCell>
              <TableCell>Submitter</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No submissions found
                </TableCell>
              </TableRow>
            ) : (
              submissions.map((submission) => (
                <TableRow
                  key={submission.id}
                  hover
                  selected={selectedId === submission.id}
                  onClick={() => onSelectSubmission?.(submission.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{submission.gameName}</TableCell>
                  <TableCell>{submission.contributorHandle || submission.contributorEmail || '—'}</TableCell>
                  <TableCell>
                    <SubmissionStatusBadge status={submission.status} />
                  </TableCell>
                  <TableCell>
                    {submission.submittedAt
                      ? format(new Date(submission.submittedAt), 'MMM d, yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectSubmission?.(submission.id);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};
